import { useLayoutEffect, useRef, type KeyboardEvent, type TextareaHTMLAttributes } from 'react';

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'rows'> & { value: string };

/**
 * "Uwagi" in one line (owner 2026-10-23 21:25: „pole uwagi niech się rozszerza, jeśli kliknę Opt+Enter”): looks and
 * behaves like a text field — Enter saves the form as before — while Option/Alt+Enter (or Shift+Enter) starts a new
 * line and the field grows with its text.
 */
export function NotesInput({ value, onKeyDown, style, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  }, [value]);

  function keys(e: KeyboardEvent<HTMLTextAreaElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented || e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    const el = e.currentTarget;
    if (e.altKey || e.shiftKey) {
      // a new line where the caret is (Safari's Option+Enter may insert nothing by itself)
      e.preventDefault();
      const { selectionStart: a, selectionEnd: b } = el;
      el.setRangeText('\n', a, b, 'end');
      el.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    // plain Enter: what it did in the one-line field — submit the form (if any), never a new line
    e.preventDefault();
    el.form?.requestSubmit();
  }

  return <textarea ref={ref} rows={1} value={value} onKeyDown={keys} style={{ resize: 'none', overflow: 'hidden', ...style }} {...rest} />;
}
